/* The rule engine — §4.1.
 *
 * Language rule (§4.2): the user's own system numbers its rules, and those
 * identifiers are useful on paper, but they never reach the interface. Every
 * message below is a sentence that explains itself without a lookup. Where an
 * identifier helps a future maintainer, it lives in a comment like this one.
 */

import { DEFAULT_LIMITS, WINDOWS, type Limits } from './limits';
import { asClause, daysSince, daysUntil, pct, plural, sentenceList, shortDate } from '../lib/format';
import { isSilenced } from '../state/silenced';
import type { Book, FullPosition } from '../state/derive';
import type { EarningsDate, Trade } from '../types';

export type Severity = 'critical' | 'warning';

/* Every check this engine runs, and what it is called when it is being switched
   on and off. The kind is the part of an alert's id before the first hyphen —
   ids are `kind` or `kind-subject`, and no kind contains one.

   These are the user's own limits, so which of them are worth hearing about is
   also theirs. A rule you have decided not to run is different from a rule you
   are quietly breaking, and an alert list nobody reads because four of its
   entries are noise is worse than a shorter one that gets read. */
export const CHECKS = [
  { kind: 'overCap', label: 'Position over its ceiling', why: 'A holding grew past the cap for its band.' },
  { kind: 'exposureOverCap', label: 'Over its ceiling once contracts count', why: 'The shares are inside the cap, but the shares and the contracts on the same company together are not.' },
  { kind: 'riskOver', label: 'Too much of the book at risk', why: 'Weight times the downside you wrote down, against your per-position ceiling.' },
  { kind: 'noThesis', label: 'Undocumented positions', why: 'A holding with no reason, abort test, review date, downside or size ceiling written down.' },
  { kind: 'reviewOverdue', label: 'Review date passed', why: 'You set a date to check a thesis and it went by.' },
  { kind: 'abortLevelCrossed', label: 'Abort level crossed', why: 'The price reached the level you said would prove you wrong.' },
  { kind: 'themeOver', label: 'Theme over its limit', why: 'Too much of the book riding on one named theme.' },
  { kind: 'cashLow', label: 'Cash below the floor', why: 'Spare cash under the floor you set.' },
  { kind: 'planBeforeEarnings', label: 'Earnings with no plan', why: 'A big position reports soon and you have written no plan for it.' },
  { kind: 'thesisChurn', label: 'Thesis rewritten repeatedly', why: 'Rewritten several times with no new numbers from the company since.' },
  { kind: 'staleResidual', label: 'Leftover position unresolved', why: 'A remnant of a rotation left sitting past the days you allowed.' },
  { kind: 'drawdownPause', label: 'Drawdown pause', why: 'Down past the point where you said you would stop opening positions.' },
  { kind: 'drawdownDeRisk', label: 'Drawdown de-risk', why: 'Down past the point where you said you would cut equity exposure.' },
  { kind: 'noMarks', label: 'No prices arriving', why: 'The book is being valued at cost because no marks came back.' },
] as const;

export type CheckKind = (typeof CHECKS)[number]['kind'];

/** The check an alert came from. */
export function kindOf(alertId: string): string {
  const cut = alertId.indexOf('-');
  return cut === -1 ? alertId : alertId.slice(0, cut);
}

/** Absent means on. A check is only silent when it was deliberately switched
    off, so a new check added later starts by being heard. */
export function checkEnabled(kind: string, off: string[] | undefined): boolean {
  return !off?.includes(kind);
}

export interface Alert {
  id: string;
  severity: Severity;
  headline: string;
  body: string;
  positionId?: string;   // ticker, when the alert belongs to one position
}

export interface EngineInput {
  book: Book;
  earnings: EarningsDate[];
  trades: Trade[];
  /** Switches the cash floor to the higher reading (§4 cashFloorHot). */
  cycleIsHot: boolean;
  /** The user's limits, defaults merged with any settings overrides. */
  limits?: Limits;
  /** Checks switched off in settings, by kind. Absent means every one runs. */
  checksOff?: string[];
  /** Checks switched off on one position rather than everywhere, as
      `TICKER:kind` — see `state/silenced.ts`. A silenced check produces no
      alert at all, so nothing downstream records it either. */
  silenced?: string[];
  /** Thresholds the user wrote themselves. */
  customRules?: CustomRule[];
  /** The size ceilings the book defines. Absent or empty stands the
      undocumented check's ceiling requirement down — see `missingParts`. */
  capBands?: import('../types').CapBandRecord[];
  now?: Date;
}

/* A limit the user wrote rather than one the spec did.
 *
 * Deliberately one shape rather than a language: a figure this book already
 * shows, a direction, and a number. Everything the built-in checks do is of
 * that form too, so nothing here is a new kind of claim — and a rule you can
 * read back in one sentence is one you can tell is wrong. */
export interface CustomRule {
  id: string;
  /** Empty means the whole book rather than one holding. */
  ticker: string;
  metric: 'weight' | 'price' | 'return' | 'value';
  direction: 'above' | 'below';
  value: number;
  severity: Severity;
  /** The user's own words, shown instead of a generated sentence when given. */
  note?: string;
}

/** A position is documented when it carries all four thesis fields and a cap
    band. §10 leaves six names without a band on purpose so they surface here. */
function missingParts(p: FullPosition, ceilingsExist: boolean): string[] {
  const missing: string[] = [];
  const t = p.thesis;
  if (!t?.why?.trim()) missing.push('reason');
  if (!t?.abortMetric?.trim()) missing.push('abort test');
  if (!t?.checkBy?.trim()) missing.push('review date');
  if (!t || !(t.lossIfWrong > 0)) missing.push('downside estimate');
  /* Resolved, not merely assigned: an id naming a ceiling that was deleted
     leaves the position uncapped, and reading it as documented was the quiet
     half of that. And where the book defines no ceilings at all, asking for one
     is asking for something nothing can supply — the user deleted them on
     purpose, and a permanent card about it is noise rather than a limit. */
  if (ceilingsExist && p.cap === null) missing.push('size ceiling');
  return missing;
}

export function runRules(input: EngineInput): Alert[] {
  const { book, earnings, trades, cycleIsHot } = input;
  const LIMITS = input.limits ?? DEFAULT_LIMITS;
  const now = input.now ?? new Date();
  const alerts: Alert[] = [];
  const { positions, totals, themes } = book;
  /* Whether the book defines any size ceilings at all. With none, nothing can
     satisfy a demand for one. */
  const ceilingsExist = (input.capBands?.length ?? 0) > 0;

  /* --- overCap: weight above its assigned ceiling --- */
  for (const p of positions) {
    /* `cap === null` covers both no assignment and one naming a ceiling the
       book no longer has. Testing `capKind` instead let a dangling id through
       as though it were capped, and then skipped it for having no number. */
    if (p.cap === null) continue;
    if (p.weight <= p.cap) continue;
    const overTrim = p.weight > p.cap * LIMITS.trimTriggerMult;
    alerts.push({
      id: `overCap-${p.ticker}`,
      severity: overTrim ? 'critical' : 'warning',
      headline: `${p.ticker} is ${pct(p.weight)}`,
      /* The name is set off in quotes rather than dropped into the sentence.
         It reads as an adjective only because the nine shipped ones are
         lowercase adjectival phrases; a ceiling somebody calls "Gambling
         position" gives "for a Gambling position position is 3.0%". Quoted, it
         reads correctly for any string anybody types. */
      body: overTrim
        ? `Your ceiling for "${p.capName}" is ${pct(p.cap)}. ` +
          `Past ${pct(p.cap * LIMITS.trimTriggerMult)} you said you would trim.`
        : `Your ceiling for "${p.capName}" is ${pct(p.cap)}.`,
      positionId: p.ticker,
    });
  }

  /* --- exposureOverCap: inside the ceiling on shares, past it on the bet ---
     A perpetual on a company already held is exposure to that company, and the
     cap is a limit on how much of the book rides on one name rather than on how
     those dollars were bought. The shares alone can sit comfortably under it
     while the shares and the contract together are well over — and because
     margin swells the book's total, opening the contract pushes the share
     weight *down*, so nothing here said a word about it.

     Only where `overCap` is silent. Where the shares are over on their own that
     card is already on the screen, and two cards for one company is how an
     alert list stops being read. */
  for (const p of positions) {
    if (p.cap === null) continue;
    if (p.contractCount === 0) continue;
    if (p.weight > p.cap) continue;          // overCap has this one
    if (p.exposureWeight <= p.cap) continue;
    const overTrim = p.exposureWeight > p.cap * LIMITS.trimTriggerMult;
    alerts.push({
      id: `exposureOverCap-${p.ticker}`,
      severity: overTrim ? 'critical' : 'warning',
      headline: `${p.ticker} is ${pct(p.exposureWeight)} once `
        + `${p.contractCount === 1 ? 'the contract counts' : 'the contracts count'}`,
      body: `The shares are ${pct(p.weight)}. ${plural(p.contractCount, 'contract')} on the same ` +
        `company ${p.contractCount === 1 ? 'carries' : 'carry'} the rest. Your ceiling for ` +
        `"${p.capName}" is ${pct(p.cap)}.`,
      positionId: p.ticker,
    });
  }

  /* --- noThesis: undocumented positions above the size worth writing up ---
     The threshold was the literal 1 here, which is the one number the engine
     compared against without asking limits.ts for it. It is a judgement about
     how small a position has to be before not knowing why you hold it is fine,
     which is exactly the kind of number §7.5 puts on the settings screen. */
  const undocumented = positions
    .filter((p) => p.weight > LIMITS.documentAbove && missingParts(p, ceilingsExist).length > 0)
    /* This one card speaks for the whole book, so a position silenced on it
       cannot be dropped by id the way the others can — it comes out of the list
       instead, and the card disappears on its own once the list is empty. */
    .filter((p) => !isSilenced(input.silenced, p.ticker, 'noThesis'))
    .sort((a, b) => b.weight - a.weight);
  if (undocumented.length > 0) {
    // The card lists the biggest offenders; the rest are a count. The full
    // detail of what each one is missing lives on its own screen.
    const SHOWN = 6;
    const lines = undocumented.slice(0, SHOWN).map(
      (p) => `${p.ticker} ${pct(p.weight)}, no ${sentenceList(missingParts(p, ceilingsExist))}`,
    );
    const rest = undocumented.length - lines.length;
    // plural() would make this "5 mores" — "more" has no plural form.
    if (rest > 0) lines.push(`…and ${rest} more.`);
    alerts.push({
      id: 'noThesis',
      severity: 'critical',
      headline: `${plural(undocumented.length, 'position')} undocumented`,
      body: lines.join('\n'),
    });
  }

  /* --- reviewOverdue: the check-by date passed with no decision logged --- */
  for (const p of positions) {
    const t = p.thesis;
    if (!t?.checkBy) continue;
    const overdueBy = daysSince(t.checkBy, now);
    if (overdueBy <= 0) continue;
    // A decision logged at or after the date closes it out.
    if (t.reviewedAt && new Date(t.reviewedAt) >= new Date(`${t.checkBy}T00:00:00Z`)) continue;
    alerts.push({
      id: `reviewOverdue-${p.ticker}`,
      severity: overdueBy > 30 ? 'critical' : 'warning',
      headline: `${p.ticker} review is ${plural(overdueBy, 'day')} overdue`,
      body: `You set ${shortDate(t.checkBy, now)} as the date to check this. It passed.`,
      positionId: p.ticker,
    });
  }

  /* --- abortLevelCrossed: the mark traded through the recorded level --- */
  for (const p of positions) {
    const t = p.thesis;
    if (!t?.abortPrice || p.priceMissing) continue;
    // Latched once crossed, so a bounce back above the level does not clear the
    // alert before the user has looked at it.
    const crossed = p.price <= t.abortPrice || Boolean(t.abortCrossedAt);
    if (!crossed) continue;
    alerts.push({
      id: `abortLevelCrossed-${p.ticker}`,
      severity: 'critical',
      headline: `${p.ticker} hit your abort level`,
      body: `You said ${asClause(t.abortMetric) || `it would break below ${t.abortPrice}`}. It happened.`,
      positionId: p.ticker,
    });
  }

  /* --- themeOver: one theme carrying too much of the book --- */
  for (const theme of themes) {
    if (theme.weight <= LIMITS.themeLimit) continue;
    alerts.push({
      id: `themeOver-${theme.name}`,
      severity: 'critical',
      headline: `${pct(theme.weight)} rides on ${theme.name}`,
      body: `${plural(theme.tickers.length, 'ticker')}, one bet. ` +
            `Your limit for a single theme is ${pct(LIMITS.themeLimit)}.`,
    });
  }

  /* --- cashLow: below the floor --- */
  const floor = cycleIsHot ? LIMITS.cashFloorHot : LIMITS.cashFloor;
  if (totals.cashPct < floor) {
    const marginUsd = totals.perpMarginUsd;
    const marginNote = marginUsd > 0
      ? ` A further ${Math.round((marginUsd / totals.totalUsd) * 1000) / 10}% is posted as futures ` +
        `margin, which cannot be spent without closing a contract.`
      : '';
    alerts.push({
      id: 'cashLow',
      severity: totals.cashPct < floor / 2 ? 'critical' : 'warning',
      headline: 'No spare cash',
      body: `Cash is ${pct(totals.cashPct)} of the book. Your floor is ${pct(floor)}` +
            `${cycleIsHot ? ', raised because you have the cycle marked hot' : ''}.${marginNote}`,
    });
  }

  /* --- riskOver: one abort firing costs too much of the book --- */
  for (const p of positions) {
    if (!p.thesis || p.riskToBookPct <= LIMITS.riskPerPosition) continue;
    alerts.push({
      id: `riskOver-${p.ticker}`,
      severity: 'critical',
      headline: `${p.ticker} risks ${pct(p.riskToBookPct)} of everything`,
      body: `At ${pct(p.weight)} of the book, a ${pct(p.thesis.lossIfWrong)} fall costs you ` +
            `${pct(p.riskToBookPct)}. Your ceiling per position is ${pct(LIMITS.riskPerPosition)}.`,
      positionId: p.ticker,
    });
  }

  /* --- planBeforeEarnings: big position, report close, nothing written --- */
  const earningsBySymbol = new Map(earnings.map((e) => [e.symbol, e]));
  for (const p of positions) {
    if (p.weight <= LIMITS.planBeforeEarningsOver) continue;
    const e = earningsBySymbol.get(p.yahooSymbol);
    // A row can carry a dividend and no earnings date; this rule is about
    // reports, so it has nothing to say about one.
    if (!e?.date) continue;
    const away = daysUntil(e.date, now);
    if (away < 0 || away > WINDOWS.earningsPlanDays) continue;
    if (p.thesis?.earningsPlan?.trim()) continue;
    alerts.push({
      id: `planBeforeEarnings-${p.ticker}`,
      severity: 'warning',
      headline: `Write your plan for ${p.ticker}`,
      body: `Earnings ${away === 0 ? 'today' : `in ${plural(away, 'day')}`}` +
            `${e.estimated ? ' (the date is still an estimate)' : ''}. ` +
            `Decide now what a miss and a beat each mean.`,
      positionId: p.ticker,
    });
  }

  /* --- marks missing: say so before any number is trusted --- */
  if (totals.unpricedCount > 0) {
    const names = positions.filter((p) => p.priceMissing).map((p) => p.ticker);
    alerts.push({
      id: 'noMarks',
      severity: 'critical',
      headline: `No price for ${plural(totals.unpricedCount, 'position')}`,
      body: `${sentenceList(names)} came back without a mark, so ${totals.unpricedCount === 1 ? 'it is' : 'they are'} ` +
            'being carried at what you paid. Every total and weight on this screen is an estimate until ' +
            'the prices come back.',
    });
  }

  /* --- drawdownPause / deRisk --- */
  /* Held back while any position is carried at cost: the peak is a measured
     market value, and comparing a cost-valued book against it would report a
     crash that never happened. */
  if (totals.unpricedCount > 0) {
    // nothing to say about the drawdown until the book is fully marked
  } else if (totals.drawdownPct > LIMITS.deRiskDrawdown) {
    alerts.push({
      id: 'drawdownDeRisk',
      severity: 'critical',
      headline: `Down ${pct(totals.drawdownPct)} from peak`,
      body: `Past ${pct(LIMITS.deRiskDrawdown)} you said you would cut equity exposure to half.`,
    });
  } else if (totals.drawdownPct > LIMITS.pauseDrawdown) {
    alerts.push({
      id: 'drawdownPause',
      severity: 'critical',
      headline: `Down ${pct(totals.drawdownPct)} from peak`,
      body: `No new positions for ${WINDOWS.pauseDays} days. Re-read every thesis.`,
    });
  }

  /* --- thesisChurn: rewritten repeatedly with no new report in between --- */
  for (const p of positions) {
    const t = p.thesis;
    if (!t?.revisions?.length) continue;
    const lastEarnings = lastEarningsDate(p, earningsBySymbol, now);
    const since = t.revisions.filter((r) => !lastEarnings || new Date(r.at) > lastEarnings);
    if (since.length < WINDOWS.thesisChurnCount) continue;
    alerts.push({
      id: `thesisChurn-${p.ticker}`,
      severity: 'warning',
      headline: `${p.ticker} thesis rewritten ${since.length}x`,
      body: 'No new numbers since the last report. Check whether the price is writing this.',
      positionId: p.ticker,
    });
  }

  /* --- staleResidual: a rotation leftover nobody resolved --- */
  for (const p of positions) {
    const residual = residualSince(p, trades, now, LIMITS.staleResidualDays);
    if (residual === null) continue;
    alerts.push({
      id: `staleResidual-${p.ticker}`,
      severity: 'warning',
      headline: `${p.ticker} leftover is ${plural(residual, 'day')} old`,
      body: `You sold most of this ${plural(residual, 'day')} ago and kept ${pct(p.weight)}. ` +
            'Close it or write down why it is still here.',
      positionId: p.ticker,
    });
  }

  /* The user's own thresholds, run after the spec's. They are alerts like any
     other and sort in among them — a limit you wrote is not a lesser limit. */
  for (const rule of input.customRules ?? []) {
    const alert = evaluateCustom(rule, book);
    if (alert) alerts.push(alert);
  }

  /* Checks switched off never reach the screen — globally by kind, or on one
     position by kind. Dropped here rather than hidden by the screen, so a
     silenced breach leaves no episode in the alert log: what the user has said
     is not a problem, the book does not keep notes about. */
  const kept = alerts.filter((a) => (
    checkEnabled(kindOf(a.id), input.checksOff)
    && !(a.positionId && isSilenced(input.silenced, a.positionId, kindOf(a.id)))
  ));

  // Critical first, and within a severity the larger exposure leads.
  const rank = { critical: 0, warning: 1 };
  return kept.sort((a, b) => rank[a.severity] - rank[b.severity]);
}

/* ---------- the user's own thresholds ---------- */

const METRIC_WORDS: Record<CustomRule['metric'], string> = {
  weight: 'weight',
  price: 'price',
  return: 'return from average cost',
  value: 'value',
};

/** What the rule is watching, or null when the book cannot answer — a rule
    about a ticker that is not held, or a price that never arrived, reports
    nothing rather than reporting zero. */
function readMetric(rule: CustomRule, book: Book): number | null {
  if (rule.ticker === '') {
    // Whole-book rules: only the two figures that mean anything at that level.
    if (rule.metric === 'value') return book.totals.totalUsd;
    if (rule.metric === 'return') return book.totals.returnOnMoneyInPct;
    return null;
  }

  const p = book.positions.find((x) => x.ticker.toUpperCase() === rule.ticker.toUpperCase());
  if (!p) return null;
  switch (rule.metric) {
    case 'weight': return p.weight;
    case 'value': return p.marketValue;
    case 'price': return p.priceMissing ? null : p.price;
    /* Return is measured from average cost. A lot with no cost recorded has
       no return, and zero would read as "flat" rather than "not known". */
    case 'return': return p.costBasis > 0 ? p.totalReturnPct : null;
  }
}

export function evaluateCustom(rule: CustomRule, book: Book): Alert | null {
  if (!Number.isFinite(rule.value)) return null;
  const reading = readMetric(rule, book);
  if (reading === null) return null;

  const crossed = rule.direction === 'above' ? reading > rule.value : reading < rule.value;
  if (!crossed) return null;

  const subject = rule.ticker === '' ? 'The book' : rule.ticker;
  const word = METRIC_WORDS[rule.metric];
  const shown = rule.metric === 'weight' || rule.metric === 'return'
    ? `${pct(reading)}`
    : `${reading.toFixed(2)}`;
  const limit = rule.metric === 'weight' || rule.metric === 'return'
    ? `${pct(rule.value)}`
    : `${rule.value.toFixed(2)}`;

  return {
    id: `custom-${rule.id}`,
    severity: rule.severity,
    headline: `${subject} ${word} is ${shown}`,
    // Their own words when they wrote any, and a plain sentence when they did
    // not — never a rule identifier (§4.2).
    body: rule.note?.trim()
      ? rule.note.trim()
      : `You asked to hear when it went ${rule.direction} ${limit}.`,
    ...(rule.ticker === '' ? {} : { positionId: rule.ticker }),
  };
}

/** The most recent earnings report already in the past. The calendar endpoint
    returns the next date, so a past one only exists once it has rolled over;
    until then churn is measured against the whole revision history. */
function lastEarningsDate(
  p: FullPosition,
  earnings: Map<string, EarningsDate>,
  now: Date,
): Date | null {
  const e = earnings.get(p.yahooSymbol);
  if (!e) return null;
  const d = new Date(`${e.date}T00:00:00Z`);
  return d < now ? d : null;
}

/** A position is a residual when a sell took most of it away, nothing has been
    bought since, and it has sat under the leftover threshold past the window. */
function residualSince(
  p: FullPosition, trades: Trade[], now: Date, staleAfterDays: number,
): number | null {
  if (p.weight >= 1) return null;
  const mine = trades
    .filter((t) => t.ticker === p.ticker)
    .sort((a, b) => b.at.localeCompare(a.at));
  const last = mine[0];
  if (!last || last.side !== 'sell' || last.reason !== 'rotate') return null;
  const age = daysSince(last.at, now);
  return age > staleAfterDays ? age : null;
}
